import React from 'react';

import { type CURRENCY, type UUID } from '@nfctron/nexus-core';
import { useFormat } from '@nfctron/nexus-react';

import { Text } from '@mantine/core';

import { RadioGroup } from '@root/features/form/components/RadioGroup/RadioGroup';

import { APP_I18N_NAMESPACE } from '@root/lib/constants/common';
import { useTranslate } from '@root/lib/hooks/useTranslation';

import { type CartStoreExtraProductsSlice } from '../../stores/cartStore';

/**
 * ProductGroupController component
 * @author filipditrich <ditrich@nfctron.com>
 * @constructor
 */
export const ProductGroupController: React.FunctionComponent<ProductGroupControllerProps> = ({ value, onChange, options, currencyId }) => {
	const { t } = useTranslate(APP_I18N_NAMESPACE.MAIN, ProductGroupController.displayName);
	const format = useFormat();

	return (
		<RadioGroup
			data={options.map(({ product, productMeta }) => ({
				/** product info */
				label: product.name,
				description: product.description,
				/** sold out product */
				...(productMeta.soldOut && {
					disabled: true,
					description: t('soldOutOption.text'),
				}),
				/** quantity limited product */
				...(productMeta.quantityLimited && {
					disabled: true,
					description: t('quantityLimitedOption.text'),
				}),
				value: product.ticketProductId,
				icon: <Text size="md">{format.price(product.price, currencyId)}</Text>,
			}))}
			value={value}
			onChange={onChange}
		/>
	);
};

/**
 * ProductGroupController component option type
 * @export
 */
export type ProductGroupControllerOption = {
	product: CartStoreExtraProductsSlice.ExtraProductConfig;
	productMeta: ReturnType<CartStoreExtraProductsSlice.Actions['getExtraProductStateById']>;
};

/**
 * ProductGroupController component props type
 * @export
 */
export type ProductGroupControllerProps = {
	options: ProductGroupControllerOption[];
	currencyId: CURRENCY;
	value?: UUID;
	onChange: (value: UUID) => void;
};

/**
 * ProductGroupController component display name
 * @type {ComponentDisplayName}
 */
ProductGroupController.displayName = 'Event/Cart/ProductGroupController';
